/**
 * MANAGEMENT (14) — the super-admin SESSION plane (the "who is a platform operator" signal). The
 * route-guard COMPONENT that consumes it lives in ./components/PlatformAdminGuard.tsx (kept separate so
 * this file exports only hooks/functions — the react-refresh single-export convention the codebase uses
 * for its api/lib split).
 *
 * The management console is a DIFFERENT authority plane from the lab cockpit. On the server the
 * `est_admin_plateforme` flag IS the authority (`authenticatePlatform` resolves it LIVE per request via
 * the un-scoped `rawPrisma`; the JWT never carries it). The client mirrors that: the console is shown ONLY
 * to a platform super-admin, and the server re-checks every request (this only hides/short-circuits UI —
 * never the trust boundary).
 *
 * HOW THE CLIENT KNOWS — ONE server-authoritative signal: `user.estAdminPlateforme` (surfaced by getMe,
 * typed on `CurrentUser`). We deliberately do NOT fall back to "holds any management.* capability": a lab
 * owner's `est_proprietaire` full-catalog effective set includes the platform caps, so a cap-based gate
 * would leak the console into the Propriétaire view. The flag is the lab-vs-platform plane boundary; the
 * server re-checks it on every request (this only hides/short-circuits UI — never the trust boundary).
 */
import { useAuthStore } from "@/auth";
import type { CurrentUser } from "@/auth";

/** The session super-admin flag — `estAdminPlateforme` is a typed `CurrentUser` field surfaced by getMe. */
function readPlatformFlag(user: CurrentUser | null): boolean {
  return user?.estAdminPlateforme === true;
}

/**
 * `true` ONLY when the live session is a platform super-admin (the server-authoritative
 * `est_admin_plateforme` flag). It deliberately does NOT key off holding a `management.*` capability — a
 * lab owner's full-catalog effective set would otherwise trip it and leak the console into the owner view.
 * Subscribes narrowly so a component re-renders only when the answer flips. THE gate every management
 * surface (route-guard, nav-guard, screens) keys off.
 */
export function useIsPlatformAdmin(): boolean {
  return useAuthStore((s) => readPlatformFlag(s.user));
}

/** Imperative variant for non-React call sites (router defaults, command-palette filtering). */
export function isPlatformAdmin(): boolean {
  return readPlatformFlag(useAuthStore.getState().user);
}
